import React, { useMemo, useState } from "react";
import { languages } from "../../../../../dummyData/dummyData";
import Table from "../../Table";
import styles from "../../Table.module.scss";
import Button from "../../../../atoms/Button/Button";
import { generatePath, useHistory } from "react-router-dom";
import { Routes } from "../../../../../routes";
import { currencyFormat, percentageFormat } from "../../../../../utils/formatters";
import Text from "../../../../atoms/Text/Text";
import DateSelect from "../../../../atoms/DateSelect/DateSelect";

function InstructorStatisticsTable({ reports }) {
  const { data } = reports;

  const allQuarters = useMemo(() => {
    const dates = new Set();

    data.forEach(({ course_separate_data: courses }) => {
      courses.forEach(({ items }) => {
        items.forEach(({ date }) => dates.add(date));
      });
    });

    const quarters = {};

    [...dates].sort().reverse().forEach((startDate) => {
      const date = new Date(startDate);
      const number = Math.floor(date.getMonth() / 3) + 1;
      const name = `Q${number} ${date.getFullYear()}`;

      if (!quarters[name]) {
        quarters[name] = {
          name,
          startDates: [],
        };
      }

      quarters[name].startDates.push(startDate);
    });

    return Object.values(quarters);
  }, [data]);

  const [currentQuarter, setCurrentQuarter] = useState(allQuarters[0]);
  const history = useHistory();

  const items = data.map((instructorData) => {
    const instructorName = instructorData.instructor.name.split(" ");
    const subRowsData = instructorData.course_separate_data.map(courseData => {
      const quarterlyMinutes = courseData.items
        .find(item => currentQuarter.startDates.includes(item.date));
      const allTimeMinutes = courseData.items
        .reduce((acc, curr) => ({
          usage: acc.usage + curr.video_usage,
          totalUsage: acc.totalUsage + curr.total_video_usage,
        }), { usage: 0, totalUsage: 0 });
      const totalRevenue = courseData.items
        .reduce((acc, curr) => acc + curr.revenue, 0);

      return {
        courses: courseData.course.display_name,
        action: generatePath(Routes.courseDetails.statistics.base, { id: courseData.course.id }),
        quarterlyMinutesPlayed: Math.round(quarterlyMinutes.video_usage / 60),
        quarterlyViewership: quarterlyMinutes.total_video_usage !== 0 ? quarterlyMinutes.video_usage / quarterlyMinutes.total_video_usage : 0,
        allTimeMinutesPlayed: Math.round(allTimeMinutes.usage / 60),
        allTimeViewership: allTimeMinutes.totalUsage !== 0 ? allTimeMinutes.usage / allTimeMinutes.totalUsage : 0,
        instructor_revenue: totalRevenue,
        current_balance: "",
      };
    });

    return {
      ...instructorData,
      id: instructorData.instructor.id,
      instructor: {
        ...instructorData.instructor,
        name: instructorName[0],
        surname: instructorName[1],
      },
      quarterlyMinutesPlayed: subRowsData.reduce((acc, curr) => acc + curr.quarterlyMinutesPlayed, 0),
      quarterlyViewership: subRowsData.reduce((acc, curr) => acc + curr.quarterlyViewership, 0),
      allTimeMinutesPlayed: subRowsData.reduce((acc, curr) => acc + curr.allTimeMinutesPlayed, 0),
      allTimeViewership: subRowsData.reduce((acc, curr) => acc + curr.allTimeViewership, 0),
      courses: `${instructorData.course_separate_data.length} ${languages.EN.labels.courses}`,
      action: generatePath(Routes.instructorDetails.base, { id: instructorData.instructor.id }),
      subRows: subRowsData,
    }
  });

  const columns = useMemo(() => [
    {
      Header: languages.EN.labels.lastName,
      accessor: "instructor.surname",
      rowSpan: 3,
    },
    {
      Header: languages.EN.labels.firstName,
      accessor: "instructor.name",
      rowSpan: 3,
    },
    {
      Header: languages.EN.labels.courses,
      accessor: "courses",
      rowSpan: 3,
    },
    {
      Header: languages.EN.labels.statistics,
      disableSortBy: true,
      headerStyle: { justifyContent: "center" },
      columns: [
        {
          id: "quarterly",
          Header: () => (
            <div className={styles.dropDownHeader}>
              <Text s12 uppercase lh24 light>
                {languages.EN.labels.quarterly}
              </Text>
              <DateSelect
                items={allQuarters}
                setOurAnswer={setCurrentQuarter}
                currName={currentQuarter.name}
              />
            </div>
          ),
          disableSortBy: true,
          headerStyle: { justifyContent: "center" },
          columns: [
            {
              Header: languages.EN.labels.minutesPlayed,
              accessor: "quarterlyMinutesPlayed",
            },
            {
              Header: languages.EN.labels.viewership,
              id: "quarterlyViewership",
              accessor: (data) => percentageFormat(data.quarterlyViewership),
            },
          ],
        },
        {
          Header: languages.EN.labels.allTime,
          disableSortBy: true,
          headerStyle: { justifyContent: "center" },
          columns: [
            {
              Header: languages.EN.labels.minutesPlayed,
              accessor: "allTimeMinutesPlayed",
            },
            {
              Header: languages.EN.labels.viewership,
              id: "allTimeViewership",
              accessor: (data) => percentageFormat(data.allTimeViewership),
            },
          ],
        },
        {
          Header: languages.EN.labels.revenue,
          disableSortBy: true,
          headerStyle: {justifyContent: "center"},
          columns: [
            {
              Header: languages.EN.labels.totalRevenue,
              accessor: "instructor_revenue",
              Cell: (data) => currencyFormat(data.row.original.instructor_revenue),
            },
            {
              Header: languages.EN.labels.currentBalance,
              accessor: "current_balance",
              Cell: (data) => data.row.original.current_balance !== "" ? currencyFormat(data.row.original.current_balance) : "",
            },
          ]
        },
      ],
    },
    {
      Header: languages.EN.labels.actions,
      disableSortBy: true,
      headerStyle: { justifyContent: "center", maxWidth: "130px" },
      rowSpan: 3,
      Cell: (data) => (
        <div className={styles.actionBox}>
          <Button
            variant="iconButton"
            onlyIcon
            icon="dots"
            onClick={() => history.push(data.row.original.action)}
            tooltip={languages.EN.labels.details}
          />
        </div>
      ),
    },
  ], []);

  return <Table columns={columns} data={items} expand={false} />;
}

export default InstructorStatisticsTable;
